import { Box, Grid, Typography } from "@material-ui/core";
import { useObserver } from "mobx-react-lite";
import React, { useState } from "react";

// Core
import { isEmptyOrWhitespace } from "Core/Utils/Utils";

// Custom
import { EditableInput } from "Custom/Components/Edits/EditableInput";
import { BuyModelDTO, UpdateStripePaymentRequestDTO } from "Custom/Models/BuyModel";
import { BuyViewModel } from "Custom/ViewModels/BuyViewModel";
import { CardPaymentViewModel } from "Custom/ViewModels/CardPaymentViewModel";
import { CardPaymentStyles } from "./CardPayment.styling";
import { StandardButton } from "../Buttons/StandardButton";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

interface IProps {
    totalPrice: number;
    propertyToProcessId: string;
    reservationRequestId: string | undefined;
    orderRequestId: string | undefined;
    currentUserId: string | undefined;
    onCancel: () => void; // Kept for backward compatibility
}

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: "#32325d",
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
                color: "#aab7c4",
            },
        },
        invalid: {
            color: "#fa755a",
            iconColor: "#fa755a",
        },
    },
    labels: {
        postalCode: "Postcode",
    },
    hidePostalCode: true,
};

export const CardPayment: React.FC<IProps> = ({ totalPrice, propertyToProcessId, reservationRequestId, orderRequestId, currentUserId }) => {
    const [viewModel] = React.useState(() => new CardPaymentViewModel());
    const [buyViewModel] = React.useState(() => BuyViewModel.Instance);
    const bind = viewModel.getContext();
    const cardPaymentStyles = CardPaymentStyles();
    const [processing, setProcessing] = useState(false);
    const [cardError, setCardError] = useState<string | undefined>(undefined);

    // Stripe hooks
    const stripe = useStripe();
    const elements = useElements();

    React.useEffect(() => {
        buyViewModel.setErrorMessage("");
    }, []);

    const handleCardChange = (event: any) => {
        setCardError(event.error ? event.error.message : "");
        viewModel.setCardComplete(event.complete); // update card details to be validated
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setProcessing(true);

        // Get the card element
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setProcessing(false);
            setCardError("Card element not found");
            return;
        }

        // set the paymentDTO
        const requests: UpdateStripePaymentRequestDTO[] = [];
        if (reservationRequestId) {
            const reservationRequest: UpdateStripePaymentRequestDTO = {
                id: reservationRequestId,
                paidByIdentityUserId: currentUserId ? currentUserId : "",
            };
            requests.push(reservationRequest);
        }
        if (orderRequestId) {
            const reservationRequest: UpdateStripePaymentRequestDTO = {
                id: orderRequestId,
                paidByIdentityUserId: currentUserId ? currentUserId : "",
            };
            requests.push(reservationRequest);
        }

        const model: BuyModelDTO = {
            name: viewModel.model.name,
            propertyId: propertyToProcessId,
            updatedStripePaymentRequests: requests,
            paymentType: "card",
            price: totalPrice,
            paymentSuccess: false,
            actionUrl: "",
            intentId: "",
            stripeResponse: "",
            paymentMethodId: "",
        };

        const result = await stripe!.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
                name: model.name,
            },
        });

        if (result.paymentMethod) {
            model.paymentMethodId = result.paymentMethod.id;
        }

        await buyViewModel.cardPayment(model);
        const stripeResponse: string = buyViewModel.getValue("stripeResponse");

        if (stripeResponse !== "") {
            let stripeResponseArray = stripeResponse.split("||");
            const clientSecret = stripeResponseArray[1];

            // Confirm the payment with Stripe.js
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: viewModel.model.name,
                    },
                },
            });

            if (result.error) {
                // Show error to your customer
                setCardError(result.error.message);
                setProcessing(false);
            } else {
                // The payment succeeded!
                if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
                    // Update the model with the payment intent ID
                    model.intentId = result.paymentIntent.id;
                    await buyViewModel.cardPayment(model);
                    setProcessing(false);
                }
            }
        } else {
            setProcessing(false);
        }
    };

    return useObserver(() => (
        <form className={cardPaymentStyles.paymentForm} onSubmit={onSubmit}>
            <Grid container spacing={2}>
                <Grid item md={12} sm={12} xs={12}>
                    <Box>
                        <EditableInput
                            fieldName={bind.name}
                            inputProps={{
                                autoComplete: "cc-name",
                                id: "card-input-name",
                                placeholder: "Name on card",
                                InputProps: {
                                    notched: false,
                                },
                                InputLabelProps: {
                                    shrink: true,
                                },
                            }}
                            label="Name on card"
                            validateOnBlur={false}
                            viewModel={viewModel}
                        />
                    </Box>
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                    <Box style={{ padding: "10px 0" }}>
                        <Typography variant="body2" style={{ marginBottom: "8px" }}>
                            Card Information
                        </Typography>
                        <div
                            style={{
                                padding: "10px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                            }}
                        >
                            <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
                        </div>
                        {cardError && (
                            <Typography variant="body2" style={{ color: "red", marginTop: "8px" }}>
                                {cardError}
                            </Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid spacing={2} container>
                        {!isEmptyOrWhitespace(buyViewModel.errorMessage) && (
                            <Grid item xs={12}>
                                <p style={{ color: "red", textAlign: "center" }}>{buyViewModel.errorMessage}</p>
                            </Grid>
                        )}
                        {(buyViewModel.IsLoading || processing) && (
                            <Grid item xs={12}>
                                <p style={{ textAlign: "center" }}>Please stand by...</p>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <StandardButton
                                canExecute={!buyViewModel.IsLoading && !processing && viewModel.areAllRequiredFieldsFilled() && !cardError}
                                className={cardPaymentStyles.payNowButton}
                                displayName={processing ? "Processing..." : "Pay now"}
                                isSubmit={true}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    ));
};
